import {
    OverlayTrigger,
    Table,
    Tooltip,
    Pagination,
} from "react-bootstrap";
import { Archive, DollarSign, Tag, UserCheck } from "react-feather";
import { formatNumberWithCommas } from "../../../../utils/utility";
import { useRef } from "react";
import "./TransactionsList.css";

export default function TransactionsList({
    transaction = [],
    onAssignSalesperson,
    onOverpaid,
    onFge,
    currentPage = 1,
    perPage = 0,
    lastPage = 1,
    onPageChange,
}) {
    const topScrollRef = useRef(null);
    const tableScrollRef = useRef(null);

    // shared scroll state (critical fix)
    const scrollLock = useRef(false);

    const columns = [
        "S.No",
        "Actions",
        "Transaction ID",
        "Payment Date",
        "Payment Time",
        "Remarks",
        "Amount",
        "Remaining Amount",
        "Invoice Number",
        "Paid Invoice",
        "Company",
        "Sales Person",
        "Submitted Date",
        "Type",
        "Status",
    ];

    const syncScroll = (source) => {
        const scrollLeft = source.target.scrollLeft;

        if (scrollLock.current) return;

        scrollLock.current = true;

        if (topScrollRef.current) {
            topScrollRef.current.scrollLeft = scrollLeft;
        }

        if (tableScrollRef.current) {
            tableScrollRef.current.scrollLeft = scrollLeft;
        }

        requestAnimationFrame(() => {
            scrollLock.current = false;
        });
    };

    const handleTopScroll = (e) => syncScroll(e);
    const handleTableScroll = (e) => syncScroll(e);

    const handlePageChangeInternal = (page) => {
        if (page >= 1 && page <= lastPage) {
            onPageChange(page);
        }
    };

    const renderPagination = () => {
        if (!Number.isFinite(lastPage) || lastPage <= 1) return null;

        const getPages = () => {
            const pages = [];

            if (lastPage <= 7) {
                for (let i = 1; i <= lastPage; i++) {
                    pages.push(i);
                }

                return pages;
            }

            pages.push(1);

            if (currentPage > 4) {
                pages.push("start-ellipsis");
            }

            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(lastPage - 1, currentPage + 1);
                i++
            ) {
                pages.push(i);
            }

            if (currentPage < lastPage - 3) {
                pages.push("end-ellipsis");
            }

            pages.push(lastPage);

            return pages;
        };

        return (
            <Pagination className="justify-content-end mt-3 mb-0">
                <Pagination.Prev
                    onClick={() => handlePageChangeInternal(currentPage - 1)}
                    disabled={currentPage === 1}
                />

                {getPages().map((page, index) =>
                    typeof page === "number" ? (
                        <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => handlePageChangeInternal(page)}
                        >
                            {page}
                        </Pagination.Item>
                    ) : (
                        <Pagination.Ellipsis key={`${page}-${index}`} disabled />
                    )
                )}

                <Pagination.Next
                    onClick={() => handlePageChangeInternal(currentPage + 1)}
                    disabled={currentPage === lastPage}
                />
            </Pagination>
        );
    };

    return (
        <div className="transactions-container">
            {/* TOP PAGINATION */}
            <div className="transactions-pagination-top">
                {renderPagination()}
            </div>

            {/* TOP SCROLLBAR */}
            <div
                ref={topScrollRef}
                className="transactions-header-scroll"
                onScroll={handleTopScroll}
            >
                <div className="scroll-inner" />
            </div>

            {/* TABLE BODY */}
            <div
                ref={tableScrollRef}
                className="transactions-table-scroll"
                onScroll={handleTableScroll}
            >
                <Table
                    hover
                    bordered
                    striped
                    responsive={false}
                    className="transactions-table"
                >
                    <thead className="table-light">
                        <tr>
                            {columns.map((text, idx) => (
                                <th className=""
                                    key={idx}
                                    style={
                                        text === "Actions"
                                            ? { minWidth: "100px" }
                                            : undefined
                                    }
                                >
                                    {text}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {transaction.length === 0 ? (
                            <tr>
                                <td colSpan={15} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            transaction.map((data, index) => (
                           <tr key={data.id ?? index}>
                                <td>
                                    {(currentPage - 1) * perPage + index + 1}
                                </td>
                                <td className="text-nowrap">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>Invoice Payment</Tooltip>
                                        }
                                    >
                                        <a
                                            href={`/tms/bank-transactions/payment/${data.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="me-1"
                                        >
                                            <DollarSign
                                                size={18}
                                                color="#6c757d"
                                            />
                                        </a>
                                    </OverlayTrigger>

                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                Assign Sales Person
                                            </Tooltip>
                                        }
                                    >
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-1"
                                            onClick={() =>
                                                onAssignSalesperson(data)
                                            }
                                        >
                                            <UserCheck
                                                size={18}
                                                color="#6c757d"
                                            />
                                        </button>
                                    </OverlayTrigger>

                                    { (data.status !== 'CLOSED' && data.remaining_amount > 0 && data.salesperson_id) && (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip>
                                                    Overpaid Transaction 
                                                </Tooltip>
                                            }
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 me-1"
                                                onClick={() =>
                                                    onOverpaid(data)
                                                }
                                            >
                                                <Tag
                                                    size={18}
                                                    color="#6c757d"
                                                />
                                            </button>
                                        </OverlayTrigger>
                                    )}
                                    { (data.status !== 'CLOSED' && data.remaining_amount > 0) && (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip>
                                                    FGE Transaction
                                                </Tooltip>
                                            }
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 me-1"
                                                onClick={() => onFge && onFge(data)}
                                            >
                                                <Archive
                                                    size={18}
                                                    color="#6c757d"
                                                >

                                                
                                                </Archive>
                                            </button>
                                        </OverlayTrigger>
                                    )}

                                </td>
                                <td>{data.reference_number || "-"}</td>
                                <td className="text-nowrap">{data.date || "-"}</td>
                                <td className="text-nowrap">{data.time || "-"}</td>
                                <td>{data.remarks || "-"}</td>

                                <td>
                                    ${formatNumberWithCommas(data.amount ?? 0)}
                                </td>
                                <td>
                                    $
                                    {formatNumberWithCommas(
                                        data.remaining_amount ?? 0
                                    )}
                                </td>
                                 <td>
                                    {data.bank_details?.length ? (
                                        data.bank_details.map((bank, idx) => {
                                            const hasInvoice = bank.invoice_id;

                                            return (
                                                <div key={bank.id || idx}>
                                                    {hasInvoice ? (
                                                        <a
                                                            href={`/tms/bank-transactions/payment/${data.id}?company_id=${bank.invoice?.company_id}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{
                                                                color: "#2563EB",
                                                                textDecoration: "underline",
                                                            }}
                                                        >
                                                            {bank.invoice_number}
                                                        </a>
                                                    ) : (
                                                        bank.invoice_number || "-"
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                    {data?.paid_invoice?.length
                                        ? data.paid_invoice.map((inv, idx) => (
                                              <span
                                                  key={idx}
                                                  style={{
                                                      color: inv.payment_alert
                                                          ? "red"
                                                          : "inherit",
                                                      marginRight: "8px",
                                                  }}
                                              >
                                                  {inv.number} (
                                                  {Number(inv.amount).toFixed(
                                                      2
                                                  )}
                                                  )
                                              </span>
                                          ))
                                        : "-"}
                                </td>
                               <td>
                                    {data.selectable_company?.length ? (
                                        data.selectable_company.map((company, idx) => (
                                            <div
                                                key={company.id || idx}
                                                className={idx > 0 ? "mt-1 pt-1 border-top" : ""}
                                            >
                                                <a
                                                    href={`/tms/bank-transactions/payment/${data.id}?company_id=${company.id}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {company.name}
                                                </a>
                                            </div>
                                        ))
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                {/* <td>{data.unique_companies || "-"}</td> */}
                                <td>{data.salesperson?.name || "-"}</td>
                                <td>{data.submitted_date || "-"}</td>
                                <td>{data.type || "-"}</td>
                                <td>{data.status || "-"}</td>
                            </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            {/* BOTTOM PAGINATION */}
            <div className="transactions-pagination-bottom">
                {renderPagination()}
            </div>
        </div>
    );
}